import fp from 'lodash/fp';
import React from 'react';
import { Col, Grid, Row, Tab, Tabs } from 'react-bootstrap';
import { connect } from 'react-redux';
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps, withStateHandlers } from "recompose";
import { bindActionCreators } from 'redux';
import { adminActions } from '../../../../state/modules/admin';
import { sessionActions } from '../../../../state/modules/session';
import { downloadBlobStreamFile, getBlobStream, getCsvStream, writeDataToCsvStream } from '../../../../utils';
import { LoadingButton } from '../../../components';
import Loading from '../../loading';
import { BusinessTable } from './business-table';
import { BusinessUserTable } from './business-user-table';
import { IndividualUserTable } from './individual-user-table';

const tabKeys = {
  individualUsers: 'individualUsers',
  businessUsers: 'businessUsers',
  businesses: 'businesses'
}

const Dashboard = ({
  toggleEstimatingUserData,
  isEstimatingUserData,
  isFetchingReport,
  activeKey,
  setActiveKey,
  downloadingProgressText,
  ...props
}) => (
    <div className='base-layout'>
      <section className='layout--header'>
        <Grid>
          <Row className='button-gutter'>
            <Col xs={12}>
              <h1>User admin</h1>
            </Col>
          </Row>
        </Grid>
      </section>
      <section id='section--admins' className='layout--section'>
        <Grid>
          <Row className='no-gutter'>
            <LoadingButton onClick={toggleEstimatingUserData} loading={isEstimatingUserData} text='Extract user data' />
            <p>{downloadingProgressText()}</p>
          </Row>

          <Row className='no-gutter top-gap'>
            <Tabs id='user-table-tabs' activeKey={activeKey} onSelect={(key) => setActiveKey(key)}>
              <Tab key={tabKeys.individualUsers} eventKey={tabKeys.individualUsers} title='Individual Users'>
                <IndividualUserTable
                  {...props} />
              </Tab>
              <Tab key={tabKeys.businessUsers} eventKey={tabKeys.businessUsers} title='Business Users'>
                <BusinessUserTable
                  {...props} />
              </Tab>
              <Tab key={tabKeys.businesses} eventKey={tabKeys.businesses} title='Businesses'>
                <BusinessTable
                  {...props} />
              </Tab>
            </Tabs>
          </Row>
        </Grid>
      </section>
    </div>
  )

const mapStateToProps = ({ admin, session, cpdCategorys, industry }) => ({
  userStat: admin.userStat,
  isFetchingReport: admin.isFetchingReport,
  cpdCategorys: cpdCategorys.cpdCategorys,
  industriesWithCpdCategorys: industry.industriesWithCpdCategorys,
  industries: industry.industries
})

const mapDispatchToProps = (dispatch) => ({
  cleanReport: bindActionCreators(adminActions.admin.cleanReport, dispatch),
  authenticate: bindActionCreators(sessionActions.session.authenticate, dispatch),
  getUserData: bindActionCreators(adminActions.admin.getUserData, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProps(
    ({ cpdCategorys, industriesWithCpdCategorys }) => ({
      exportUserData: (userStat) => {
        const cpdCategoryColumns = fp.flow(
          fp.sortBy('name'),
          fp.map(
            ({ id, name }) => {
              const industryNames = fp.flow(
                fp.filter(
                  ({ cpdCategorys }) => fp.includes(id)(fp.map('id')(cpdCategorys))
                ),
                fp.map('industry.name')
              )(industriesWithCpdCategorys)
              const joinedIndustryNames = industryNames.length > 0 ? `(${industryNames.join(',')})` : ''
              return `${name}${joinedIndustryNames}`
            }
          )
        )(cpdCategorys)
        const columns = [
          "Caddie ID", "Auth0 ID", "Stripe ID", "Email Address", "First Name", "Last Name", "Role", "isPublisher", "User Status", "User Type", 
          "Registration Date", "Account Closing Date", "Next Billing Date", "Business Name", "Business Notes", "Last Invite From", "Last Invite Sent Time",
          "requiredCPDPoints", "receivedCPDPoints", "gapCPDPoints", "Payment status", "AFSL name", "CPD year end date", "Last activity date", "Industry", "isDemo", 'trainingPlanHistory'
        ].concat(cpdCategoryColumns)
        const rows = fp.map(
          stat => fp.map(
            columnName => stat[columnName]
          )(columns)
        )(userStat)
        downloadBlobStreamFile(writeDataToCsvStream(getCsvStream({
          header: true,
          columns
        }), rows).pipe(getBlobStream()), 'user-data.csv');
      }
    })
  ),
  lifecycle({
    componentDidMount() {
      const {
        cleanReport,
      } = this.props;

      cleanReport();
    }
  }),
  branch(
    ({ industries }) => fp.isEmpty(industries),
    renderComponent(Loading)
  ),
  withStateHandlers(
    {
      businessQuery: null,
      activeKey: tabKeys.individualUsers,
      isEstimatingUserData: false,
      fetchingProgress: 0,
    },
    {
      setBusinessQuery: () => (businessQuery) => ({ businessQuery }),
      setActiveKey: () => (activeKey) => ({ activeKey }),
      toggleEstimatingUserData: ({ isEstimatingUserData }) => () => ({
        isEstimatingUserData: !isEstimatingUserData
      }),
      setFetchingProgress: () => (fetchingProgress) => ({
        fetchingProgress: fetchingProgress > 1 ? 1 : fetchingProgress
      })
    }
  ),
  withHandlers({
    handleBusinessQueryUpdate: ({ setBusinessQuery, setActiveKey }) => (business) => {
      setBusinessQuery(business)
      setActiveKey(tabKeys.businessUsers)
    },
    downloadingProgressText: ({ isEstimatingUserData, fetchingProgress }) => () => (
      isEstimatingUserData ? `Downloading... ${(100 * fetchingProgress).toFixed(0)}%` : ""
    )
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      const {
        userStat,
        exportUserData,
        cleanReport,
        isEstimatingUserData,
        getUserData,
        isFetchingReport,
        toggleEstimatingUserData,
        setFetchingProgress,
      } = this.props

      if (isEstimatingUserData && !isFetchingReport) {
        setFetchingProgress(userStat ? (userStat.skip + userStat.take) / userStat.hits : 0)
        const fetchingCompleted = userStat && userStat.completed
        if (!fetchingCompleted) {
          const filter = {
            skip: userStat ? userStat.result.length : 0,
            take: 32 // This works at 64 but breaks for 96 on staging. I'm not sure why 96 breaks. But keeping at 32 to be safe.
          }
          getUserData(filter)
        } else {
          const data = userStat.result.concat(userStat.pending)
          exportUserData(data)
          cleanReport()
          toggleEstimatingUserData()
        }
      }
    }
  })
)(Dashboard)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/user-portal/dashboard.js